import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

interface StreamInfoState {
	reasoningLog: string;
	searchQueries: string[];
	transformedQuery: string;
	isWriting: boolean;
}

interface StreamingContextType {
	streamInfo: StreamInfoState;
	updateStreamInfo: (updates: Partial<StreamInfoState>) => void;
	clearStreamInfo: () => void;
}

const StreamingContext = createContext<StreamingContextType | undefined>(undefined);

const initialState: StreamInfoState = {
	reasoningLog: '',
	searchQueries: [],
	transformedQuery: '',
	isWriting: false
};

export const StreamingContextProvider = ({ children }: { children: ReactNode }) => {
	const [streamInfo, setStreamInfo] = useState<StreamInfoState>(initialState);

	const updateStreamInfo = (updates: Partial<StreamInfoState>) => {
		setStreamInfo((prev) => ({
			...prev,
			reasoningLog: updates.reasoningLog ?? prev.reasoningLog,
			transformedQuery: updates.transformedQuery ?? prev.transformedQuery,
			searchQueries: [...prev.searchQueries, ...(updates.searchQueries || [])],
			isWriting: updates.isWriting ?? prev.isWriting
		}));
	};

	const clearStreamInfo = () => setStreamInfo(initialState);

	const value = useMemo(() => ({ streamInfo, updateStreamInfo, clearStreamInfo }), [streamInfo]);

	return <StreamingContext.Provider value={value}>{children}</StreamingContext.Provider>;
};

export const useStreaming = (): StreamingContextType => {
	const context = useContext(StreamingContext);
	if (!context) {
		throw new Error('useStreaming must be used within a StreamingContextProvider');
	}
	return context;
};
